import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import type { PersonalizedTaskStatus } from '@change-corgi/middle-end-gql-schema';

import { PERSONALIZED_TASK_STATUSES } from '../../constants';

type TaskExpansionRow = Readonly<{
	id: string;
	status: PersonalizedTaskStatus;
}>;

type UseTaskExpansionReturn = Readonly<{
	isExpanded: (taskId: string) => boolean;
	toggleExpansion: (taskId: string) => void;
}>;

function isAllCompleted(tasks: readonly TaskExpansionRow[]): boolean {
	return tasks.length > 0 && tasks.every((t) => t.status === PERSONALIZED_TASK_STATUSES.COMPLETED);
}

/**
 * Accordion state keyed by task id so expansion stays on the same task when the list re-sorts.
 * `tasks` is the displayed slice (e.g. view more/less); if the expanded id is temporarily missing
 * from that slice, we keep it so the same row opens again when it becomes visible.
 * Until the user toggles, the first visible task defaults to expanded when the list has any
 * non-completed task.
 */
export function useTaskExpansion(tasks: readonly TaskExpansionRow[]): UseTaskExpansionReturn {
	const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
	const userToggledRef = useRef(false);

	useLayoutEffect(() => {
		setExpandedTaskId((current) => {
			if (tasks.length === 0 || (current === null && (userToggledRef.current || isAllCompleted(tasks)))) {
				return null;
			}

			if (current !== null) {
				return current;
			}
			return tasks[0].id;
		});
	}, [tasks]);

	const isExpanded = useCallback((taskId: string) => expandedTaskId === taskId, [expandedTaskId]);

	const toggleExpansion = useCallback((taskId: string) => {
		userToggledRef.current = true;
		setExpandedTaskId((current) => (current === taskId ? null : taskId));
	}, []);

	return { isExpanded, toggleExpansion };
}
